import React, { useCallback, useEffect, useState } from 'react';
import {
  Table, Button, Modal, Form, Input, DatePicker, Checkbox, Popconfirm, Select,
  Space, Tag, Tooltip, Typography, Alert, Row, Col, Divider, message,
} from 'antd';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined, CheckCircleOutlined,
  CloudDownloadOutlined, WarningOutlined,
} from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../../authConfig';
import { fetchWithRetry } from '../utils/fetchWithRetry';

/*
 * Admin > Masters > Campaigns
 * Every ACE/AVIA campaign lives in one CampaignTable and its nominations in one Awards table.
 * Exactly one campaign is active: new nominations are tagged with it, and every nomination
 * screen, count and reminder only shows the active campaign. Starting a new cycle = create a
 * campaign and make it active - no new Awards table.
 */

const { Text } = Typography;
const { RangePicker } = DatePicker;
const BASE = process.env.REACT_APP_API_URL;
const DATE = 'YYYY-MM-DD';
const SHOW = 'DD MMM YYYY';

type Stage = 'review' | 'elt' | 'final' | 'closed';

interface Campaign {
  id: number;
  campaignName: string | null;
  campaignStatus: string | null;
  nominationStartDate: string | null;
  nominationEndDate: string | null;
  peopleManagerStartDate: string | null;
  peopleManagerEndDate: string | null;
  firstLevelManagerStartDate: string | null;
  firstLevelManagerEndDate: string | null;
  secondManagerStartDate: string | null;
  secondManagerEndDate: string | null;
  stackStartDate: string | null;
  stackEndDate: string | null;
  finalReviewStartDate: string | null;
  finalReviewEndDate: string | null;
  stage: Stage;
  nominationCount: number;
}

interface PreviewRow {
  approvalId: number;
  rewardName: string | null;
  nomineeName: string | null;
  teamName: string | null;
  nominatorName: string | null;
  nominationDate: string | null;
  managerName: string | null;
  managerEmail: string | null;
  firstLevelApproverName: string | null;
  firstLevelApproverEmail: string | null;
  firstReviewer: string | null;
  warnings: string[];
}

interface LoadPreview {
  campaignId: number;
  campaignName: string | null;
  from: string;
  to: string;
  rows: PreviewRow[];
  alreadyLoaded: number;
}

interface LoadResult {
  loaded: number;
  alreadyLoaded: number;
  emailsSent: number;
  missingFirstLevel: number;
  missingSecondLevel: number;
}

// Shown once in the summary instead of on every row
const ELT_TO_SET = 'Second level (ELT) approver to be set by an admin.';

const STAGES: { value: Stage; label: string; color: string; help: string }[] = [
  { value: 'review', label: 'Manager / Unit Head review', color: 'blue',   help: 'Sign-in sends people managers and first / second level approvers to their review pages.' },
  { value: 'elt',    label: 'ELT group review',           color: 'purple', help: 'Sign-in sends ELT members to stack ranking.' },
  { value: 'final',  label: 'Final review',               color: 'gold',   help: 'Sign-in sends super admins to the final review.' },
  { value: 'closed', label: 'Closed',                     color: 'default', help: 'No review stage is open.' },
];
const stageInfo = (s: Stage) => STAGES.find(x => x.value === s) ?? STAGES[3];

// Stage date ranges: form field -> API start / end fields; remind = gets automatic reminder emails
const RANGES = [
  { field: 'nomination',    label: 'Nomination',                     start: 'nominationStartDate',        end: 'nominationEndDate',        remind: false },
  { field: 'peopleManager', label: "People's manager review",        start: 'peopleManagerStartDate',     end: 'peopleManagerEndDate',     remind: true },
  { field: 'firstLevel',    label: 'First level (Unit Head) review', start: 'firstLevelManagerStartDate', end: 'firstLevelManagerEndDate', remind: true },
  { field: 'secondLevel',   label: 'Second level review',            start: 'secondManagerStartDate',     end: 'secondManagerEndDate',     remind: true },
  { field: 'stack',         label: 'ELT stack ranking',              start: 'stackStartDate',             end: 'stackEndDate',             remind: true },
  { field: 'finalReview',   label: 'Final review',                   start: 'finalReviewStartDate',       end: 'finalReviewEndDate',       remind: false },
] as const;

// Same rule as the API (CampaignReminderService.ReminderDays): 2 days before the end, if still
// inside the stage, and the end date itself
const reminderDays = (range?: [Dayjs | null, Dayjs | null] | null): Dayjs[] => {
  const end = range?.[1];
  if (!end) return [];
  const early = end.subtract(2, 'day');
  const start = range?.[0];
  return !start || !early.isBefore(start, 'day') ? [early, end] : [end];
};

const toDay = (v: string | null | undefined): Dayjs | null => (v ? dayjs(v) : null);
const fmt = (v: string | null | undefined) => (v ? dayjs(v).format(SHOW) : '—');

const CampaignsMaster: React.FC = () => {
  const { instance, accounts } = useMsal();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm();
  const [loadFor, setLoadFor] = useState<Campaign | null>(null);
  const [preview, setPreview] = useState<LoadPreview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [loadingNominations, setLoadingNominations] = useState(false);
  const [loadResult, setLoadResult] = useState<LoadResult | null>(null);

  const fetchToken = useCallback(async () => {
    const res = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
    return res.idToken;
  }, [instance, accounts]);

  // API errors come back as plain text (e.g. "A campaign named 'X' already exists.")
  const call = useCallback(async (path: string, method = 'GET', body?: unknown) => {
    const token = await fetchToken();
    const res = await fetchWithRetry(`${BASE}/api/campaigns${path}`, {
      method,
      headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    if (!res.ok) {
      const text = (await res.text()).replace(/^"|"$/g, '');
      throw new Error(text || `Request failed (${res.status}).`);
    }
    const text = await res.text();
    return text ? JSON.parse(text) : null;
  }, [fetchToken]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setCampaigns((await call('')) ?? []);
    } catch (e: any) {
      message.error(e?.message || 'Failed to load campaigns.');
    } finally {
      setLoading(false);
    }
  }, [call]);

  useEffect(() => { load(); }, [load]);

  const active = campaigns.find(c => c.campaignStatus === 'active');

  /* ── Add / edit ── */
  const openAdd = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ activate: false });
    setModalOpen(true);
  };

  const openEdit = (c: Campaign) => {
    setEditing(c);
    form.resetFields();
    const values: Record<string, unknown> = { campaignName: c.campaignName };
    RANGES.forEach(r => {
      const start = toDay(c[r.start]);
      const end = toDay(c[r.end]);
      values[r.field] = start || end ? [start, end] : undefined;
    });
    form.setFieldsValue(values);
    setModalOpen(true);
  };

  const handleSave = async () => {
    let values: any;
    try { values = await form.validateFields(); } catch { return; }

    const body: Record<string, unknown> = { campaignName: values.campaignName?.trim() };
    RANGES.forEach(r => {
      const range: [Dayjs | null, Dayjs | null] | undefined = values[r.field];
      body[r.start] = range?.[0] ? range[0].format(DATE) : null;
      body[r.end] = range?.[1] ? range[1].format(DATE) : null;
    });

    setSaving(true);
    try {
      if (editing) {
        await call(`/${editing.id}`, 'PUT', body);
        message.success('Campaign updated.');
      } else {
        await call('', 'POST', { ...body, activate: !!values.activate });
        message.success(values.activate ? 'Campaign created and made active.' : 'Campaign created.');
      }
      setModalOpen(false);
      load();
    } catch (e: any) {
      message.error(e?.message || 'Failed to save the campaign.');
    } finally {
      setSaving(false);
    }
  };

  /* ── Row actions ── */
  const runRowAction = async (id: number, action: () => Promise<unknown>, done: string) => {
    setBusyId(id);
    try {
      await action();
      message.success(done);
      load();
    } catch (e: any) {
      message.error(e?.message || 'The change was not saved.');
    } finally {
      setBusyId(null);
    }
  };

  const activate = (c: Campaign) =>
    runRowAction(c.id, () => call(`/${c.id}/activate`, 'POST'), `'${c.campaignName}' is now the active campaign.`);

  const changeStage = (c: Campaign, stage: Stage) =>
    runRowAction(c.id, () => call(`/${c.id}/stage`, 'PUT', { stage }), `Stage set to ${stageInfo(stage).label}.`);

  const remove = (c: Campaign) =>
    runRowAction(c.id, () => call(`/${c.id}`, 'DELETE'), 'Campaign deleted.');

  /* ── Load nominations: preview first, then load ── */
  const openLoad = async (c: Campaign) => {
    setLoadFor(c);
    setPreview(null);
    setLoadResult(null);
    setPreviewing(true);
    try {
      setPreview(await call(`/${c.id}/load-preview`));
    } catch (e: any) {
      message.error(e?.message || 'Could not check for nominations.');
      setLoadFor(null);
    } finally {
      setPreviewing(false);
    }
  };

  const runLoad = async () => {
    if (!loadFor) return;
    setLoadingNominations(true);
    try {
      const result: LoadResult = await call(`/${loadFor.id}/load`, 'POST');
      setLoadResult(result);
      message.success(`${result.loaded} nomination${result.loaded === 1 ? '' : 's'} loaded.`);
      load();
    } catch (e: any) {
      message.error(e?.message || 'The nominations were not loaded.');
    } finally {
      setLoadingNominations(false);
    }
  };

  const previewColumns = [
    { title: 'Approval ID', dataIndex: 'approvalId', key: 'approvalId', width: 100 },
    { title: 'Award', dataIndex: 'rewardName', key: 'rewardName', width: 150 },
    {
      title: 'Nominee', key: 'nominee',
      render: (_: unknown, r: PreviewRow) => (
        <Space direction="vertical" size={0}>
          <Text>{r.teamName || r.nomineeName}</Text>
          {r.teamName && <Text type="secondary" style={{ fontSize: 12 }}>{r.nomineeName}</Text>}
          <Text type="secondary" style={{ fontSize: 12 }}>by {r.nominatorName} · {fmt(r.nominationDate)}</Text>
        </Space>
      ),
    },
    {
      title: 'Manager', key: 'manager', width: 180,
      render: (_: unknown, r: PreviewRow) => r.managerName || <Text type="danger">Not found</Text>,
    },
    {
      title: 'Unit Head (Level 1)', key: 'uh', width: 180,
      render: (_: unknown, r: PreviewRow) => r.firstLevelApproverName || <Text type="danger">Not found</Text>,
    },
    {
      title: 'Check', key: 'warnings', width: 220,
      render: (_: unknown, r: PreviewRow) => {
        const w = r.warnings.filter(x => x !== ELT_TO_SET);
        return w.length
          ? <Space direction="vertical" size={0}>{w.map(x => <Text key={x} type="warning" style={{ fontSize: 12 }}><WarningOutlined /> {x}</Text>)}</Space>
          : <Text type="success" style={{ fontSize: 12 }}>OK</Text>;
      },
    },
  ];

  const columns = [
    {
      title: 'Campaign',
      dataIndex: 'campaignName',
      key: 'campaignName',
      render: (name: string | null, c: Campaign) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name || <Text type="secondary">(no name)</Text>}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>Id {c.id}</Text>
        </Space>
      ),
    },
    {
      title: 'Status',
      key: 'status',
      width: 110,
      render: (_: unknown, c: Campaign) =>
        c.campaignStatus === 'active'
          ? <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>
          : <Tag>Inactive</Tag>,
    },
    {
      title: 'Review stage',
      key: 'stage',
      width: 250,
      render: (_: unknown, c: Campaign) => (
        <Tooltip title={c.campaignStatus === 'active' ? stageInfo(c.stage).help : 'Takes effect when this campaign is active.'}>
          <Select<Stage>
            size="small"
            style={{ width: 230 }}
            value={c.stage}
            disabled={busyId === c.id}
            onChange={v => changeStage(c, v)}
            options={STAGES.map(s => ({ value: s.value, label: <Tag color={s.color}>{s.label}</Tag> }))}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Nomination window',
      key: 'window',
      width: 220,
      render: (_: unknown, c: Campaign) => `${fmt(c.nominationStartDate)} – ${fmt(c.nominationEndDate)}`,
    },
    {
      title: 'Nominations',
      dataIndex: 'nominationCount',
      key: 'nominationCount',
      width: 110,
      align: 'right' as const,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 380,
      render: (_: unknown, c: Campaign) => {
        const isActive = c.campaignStatus === 'active';
        const cannotDelete = isActive
          ? 'The active campaign cannot be deleted.'
          : c.nominationCount > 0 ? 'Campaigns with nominations are kept.' : '';
        return (
          <Space>
            {isActive && (
              <Tooltip title="Load the ACE/AVIA nominations submitted during the nomination dates">
                <Button size="small" type="primary" icon={<CloudDownloadOutlined />}
                  style={{ background: '#8629A4', borderColor: '#8629A4' }}
                  loading={previewing && loadFor?.id === c.id}
                  onClick={() => openLoad(c)}>
                  Load nominations
                </Button>
              </Tooltip>
            )}
            <Tooltip title="Edit name and stage dates">
              <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(c)} />
            </Tooltip>
            {!isActive && (
              <Popconfirm
                title={`Make '${c.campaignName}' the active campaign?`}
                description={
                  <div style={{ maxWidth: 320 }}>
                    {active ? <>'{active.campaignName}' becomes inactive. </> : null}
                    New nominations go to this campaign, and every nomination screen, count and
                    reminder switches to its nominations.
                  </div>
                }
                okText="Make active"
                onConfirm={() => activate(c)}
              >
                <Button size="small" type="primary" ghost loading={busyId === c.id}>Make active</Button>
              </Popconfirm>
            )}
            <Popconfirm
              title={`Delete '${c.campaignName}'?`}
              okText="Delete"
              okButtonProps={{ danger: true }}
              disabled={!!cannotDelete}
              onConfirm={() => remove(c)}
            >
              <Tooltip title={cannotDelete || 'Delete'}>
                <Button size="small" danger icon={<DeleteOutlined />} disabled={!!cannotDelete} />
              </Tooltip>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <div>
      {!loading && !active && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="No campaign is active"
          description="Nominations cannot be created or uploaded, and nomination screens show nothing, until a campaign is made active."
        />
      )}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, gap: 12, flexWrap: 'wrap' }}>
        <Text type="secondary">
          One campaign is active at a time. To start a new cycle, add a campaign and make it active.
          Past campaigns and their nominations are kept.
        </Text>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>Refresh</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}
            style={{ background: '#8629A4', borderColor: '#8629A4' }}>
            Add campaign
          </Button>
        </Space>
      </div>

      <Table<Campaign>
        rowKey="id"
        loading={loading}
        dataSource={campaigns}
        columns={columns}
        pagination={{ pageSize: 10, hideOnSinglePage: true }}
        scroll={{ x: 1250 }}
        size="middle"
      />

      <Modal
        title={`Load nominations: ${loadFor?.campaignName ?? ''}`}
        open={!!loadFor && !previewing}
        onCancel={() => { if (!loadingNominations) setLoadFor(null); }}
        width={1100}
        destroyOnClose
        footer={loadResult ? [
          <Button key="close" type="primary" onClick={() => setLoadFor(null)}>Close</Button>,
        ] : [
          <Button key="cancel" onClick={() => setLoadFor(null)} disabled={loadingNominations}>Cancel</Button>,
          <Popconfirm
            key="load"
            title={`Load ${preview?.rows.length ?? 0} nominations?`}
            description="They are added to this campaign and each first reviewer is emailed."
            okText="Load"
            onConfirm={runLoad}
            disabled={!preview?.rows.length}
          >
            <Button type="primary" icon={<CloudDownloadOutlined />} loading={loadingNominations}
              disabled={!preview?.rows.length}
              style={preview?.rows.length ? { background: '#8629A4', borderColor: '#8629A4' } : undefined}>
              Load {preview?.rows.length ?? 0} nomination{preview?.rows.length === 1 ? '' : 's'} and email reviewers
            </Button>
          </Popconfirm>,
        ]}
      >
        {loadResult ? (
          <Alert
            type="success"
            showIcon
            message={`${loadResult.loaded} nomination${loadResult.loaded === 1 ? '' : 's'} loaded, ${loadResult.emailsSent} review email${loadResult.emailsSent === 1 ? '' : 's'} sent.`}
            description={
              <div>
                {loadResult.alreadyLoaded > 0 && <div>{loadResult.alreadyLoaded} were already loaded and were skipped.</div>}
                {loadResult.missingFirstLevel > 0 && <div>{loadResult.missingFirstLevel} have no Unit Head.</div>}
                {loadResult.missingSecondLevel > 0 && (
                  <div>
                    {loadResult.missingSecondLevel} need their second level (ELT) approver. Set it, the ELT panel and the
                    final approver with Edit on Admin &gt; ACE/AVIA Awards (the ELT panel defaults from ELT Emails).
                  </div>
                )}
              </div>
            }
          />
        ) : preview && (
          <>
            <Alert
              type={preview.rows.length ? 'info' : 'warning'}
              showIcon
              style={{ marginBottom: 16 }}
              message={preview.rows.length
                ? `${preview.rows.length} new ACE/AVIA nomination${preview.rows.length === 1 ? '' : 's'} submitted between ${fmt(preview.from)} and ${fmt(preview.to)}.`
                : `No new ACE/AVIA nominations between ${fmt(preview.from)} and ${fmt(preview.to)}.`}
              description={
                <div>
                  {preview.alreadyLoaded > 0 && <div>{preview.alreadyLoaded} in this date range are already loaded and will be skipped.</div>}
                  <div>
                    Manager and Unit Head are filled in from the nominee's reporting line. The second level (ELT)
                    approver, ELT panel and final approver are set afterwards on Admin &gt; ACE/AVIA Awards.
                  </div>
                </div>
              }
            />
            {preview.rows.length > 0 && (
              <Table<PreviewRow>
                rowKey="approvalId"
                size="small"
                dataSource={preview.rows}
                columns={previewColumns}
                pagination={{ pageSize: 10, hideOnSinglePage: true }}
                scroll={{ x: 1000 }}
              />
            )}
          </>
        )}
      </Modal>

      <Modal
        title={editing ? `Edit campaign: ${editing.campaignName ?? ''}` : 'Add campaign'}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={handleSave}
        okText={editing ? 'Save' : 'Create'}
        confirmLoading={saving}
        width={860}
        destroyOnClose
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="campaignName"
            label="Campaign name"
            rules={[{ required: true, whitespace: true, message: 'Enter the campaign name' }]}
          >
            <Input placeholder="e.g. ACE Awards 2026 H2" maxLength={200} />
          </Form.Item>

          <Divider orientation="left" plain>Stage dates</Divider>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message="Stage dates are required: the emails show them (for example 'until 14th July'), and the email header period (H1 / H2) comes from the nomination start date."
            description="Reminders are automatic: at 5:00 pm IST two days before each review stage ends and on its last day, to people who still have a review waiting at that stage (managers, unit heads, ELT approvers, ELT panel members without a ranking response). One email per person, active campaign only."
          />
          {RANGES.map(r => (
            <Row gutter={16} key={r.field}>
              <Col xs={24}>
                <Form.Item
                  name={r.field}
                  label={r.label}
                  style={r.remind ? { marginBottom: 4 } : undefined}
                  rules={[{
                    validator: (_, v) => (v?.[0] && v?.[1]
                      ? Promise.resolve()
                      : Promise.reject(new Error('Pick the start and end date (shown in the emails)'))),
                  }]}
                >
                  <RangePicker style={{ width: '100%' }} format={SHOW} />
                </Form.Item>
                {r.remind && (
                  <Form.Item noStyle shouldUpdate={(a, b) => a[r.field] !== b[r.field]}>
                    {({ getFieldValue }) => {
                      const days = reminderDays(getFieldValue(r.field));
                      return (
                        <div style={{ marginBottom: 20 }}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {days.length
                              ? `Reminders at 5 pm IST: ${days.map(d => d.format('D MMM')).join(' and ')}`
                              : 'Reminders: pick the end date'}
                          </Text>
                        </div>
                      );
                    }}
                  </Form.Item>
                )}
              </Col>
            </Row>
          ))}

          {!editing && (
            <Form.Item name="activate" valuePropName="checked" style={{ marginBottom: 0 }}>
              <Checkbox>
                Make this the active campaign now
                {active ? <Text type="secondary"> ('{active.campaignName}' becomes inactive)</Text> : null}
              </Checkbox>
            </Form.Item>
          )}
        </Form>
      </Modal>
    </div>
  );
};

export default CampaignsMaster;
